import { useCallback, useEffect, useState } from "react";
import {
  extractSubscriptionDetails,
  getOrCreatePushSubscription,
  registerServiceWorker,
} from "./webPushUtils";
import { WebPushRegistrationDto } from "./WebPushRegistrationDto";
import { usePushNotificationDao } from "./usePushNotificationDao";
import { PushNotificationContextType } from "./PushNotificationContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const VAPID_PUBLIC_KEY =
  "BNg7oyqyy_Dz5Si7-Ji8zdCH0wnVhpmFQEg-X3DLW61-JTlgxyC9Td_XOXM9p5a0HqwXzW0zhNHKBZAyK541ORE";
const SW_URL = "/service-worker.js"; // Path to your service worker file

export const usePushNotificationContext = (): PushNotificationContextType => {
  const pushNotificationDao = usePushNotificationDao();
  const [permission, setPermission] = useState<NotificationPermission>(
    Notification.permission
  );
  const [subscription, setSubscription] = useState<PushSubscription | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const persistWebPushRegistration = useMutation({
    mutationFn: (registration: WebPushRegistrationDto) => {
      return pushNotificationDao.subscribeToWebNotifications(registration);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["PushNotificationDao.getSubscriptions"],
      });
    },
  });
  const unsubscribeFromWebNotifications = useMutation({
    mutationFn: (subscriptionId: string) => {
      return pushNotificationDao.unsubscribeFromWebNotifications(
        subscriptionId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["PushNotificationDao.getSubscriptions"],
      });
    },
  });
  // Effect for service worker registration and initial subscription check
  useEffect(() => {
    const initServiceWorker = async () => {
      setError(null);
      try {
        const registration = await registerServiceWorker(SW_URL);
        const existingSubscription =
          await registration.pushManager.getSubscription();
        if (existingSubscription) {
          setSubscription(existingSubscription);
        }

        const subscriptions = await pushNotificationDao.getSubscriptions();
        console.log("Subscriptions from server:", subscriptions);
      } catch (err: any) {
        console.error("Service Worker initialization failed:", err);
        setError(err.message || "Failed to initialize push notifications.");
      }
    };

    initServiceWorker();
  }, []); // Run once on mount

  // Function to ask for permission and subscribe
  const subscribe =
    useCallback(async (): Promise<WebPushRegistrationDto | null> => {
      setIsLoading(true);
      setError(null);

      if (!("Notification" in window)) {
        setError("Notifications are not supported by your browser.");
        setIsLoading(false);
        return null;
      }

      try {
        // 1. Request Notification Permission
        const permissionResult = await Notification.requestPermission();
        setPermission(permissionResult);

        if (permissionResult !== "granted") {
          setError("Notification permission denied.");
          setIsLoading(false);
          return null;
        }

        // 2. Get Service Worker Registration
        const registration = await navigator.serviceWorker.ready;

        // 3. Get / Create Push Subscription
        const newSubscription = await getOrCreatePushSubscription(
          registration,
          VAPID_PUBLIC_KEY
        );
        setSubscription(newSubscription);

        // 4. Extract details for your DTO
        const registrationDto = extractSubscriptionDetails(newSubscription);

        // 5. Send to server
        await persistWebPushRegistration.mutate(registrationDto);

        setIsLoading(false);
        return registrationDto;
      } catch (e: any) {
        console.error("Error subscribing user:", e);
        setError(e.message || "An unknown error occurred during subscription.");
        setIsLoading(false);
        return null;
      }
    }, [persistWebPushRegistration]); // Dependencies: None, as functions are stable and constants are external

  const unsubscribe = useCallback(
    async (subscriptionId: string) => {
      setIsLoading(true);
      setError(null);
      try {
        if (subscription) {
          await subscription.unsubscribe();
          setSubscription(null);
          console.log("User unsubscribed from browser.");
        }
        // Optionally, send unsubscription to server
        await unsubscribeFromWebNotifications.mutate(subscriptionId); // Implement this in webPushUtils if needed
        setIsLoading(false);
      } catch (e: any) {
        console.error("Error unsubscribing user:", e);
        setError(
          e.message || "An unknown error occurred during unsubscription."
        );
        setIsLoading(false);
      }
    },
    [subscription, unsubscribeFromWebNotifications]
  ); // Dependency: subscription (to ensure we unsubscribe the correct one)

  return {
    permission,
    subscription,
    isSubscribed: !!subscription,
    isLoading,
    error,
    subscribeWeb: subscribe,
    unsubscribe,
  };
};
